import { useState, useCallback } from 'react';

/**
 * useLeaderNavigation - Custom Hook zur Verwaltung der Navigations-Logik im Leader Tool.
 * Kapselt den Edit-Stack (Verlauf), die Modal-Steuerung für Termine und Export-Shortcuts.
 * 
 * @param {Object} params
 * @param {Array} params.allAssignments - Alle Einsätze (für Export-Initialisierung)
 * @param {Function} params.loadAllAssignments - Funktion zum Nachladen aller Einsätze
 * @param {Function} params.setExportSelectedIds - Setter für gewählte IDs im Export
 * @param {Function} params.setExportInitialFilters - Setter für Initialfilter im Export
 * @param {Function} params.setExportOpen - Setter zum Öffnen des Export-Modals
 * @returns {Object} Navigations-State und Handler
 */
export const useLeaderNavigation = ({
    allAssignments,
    loadAllAssignments,
    setExportSelectedIds,
    setExportInitialFilters,
    setExportOpen
}) => {
    // --- Navigation State ---
    const [editStack, setEditStack] = useState([]);
    const [forwardStack, setForwardStack] = useState([]);
    const [editingAssignment, setEditingAssignment] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [presetDate, setPresetDate] = useState(null);
    const [isUndatedPreset, setIsUndatedPreset] = useState(false);
    const [navigationSource, setNavigationSource] = useState(null);
    const [activeResources, setActiveResources] = useState({ workers: [], vehicles: [] });
    const [activeDateRange, setActiveDateRange] = useState({ start: null, end: null });

    /**
     * Öffnet den Editor für einen spezifischen Einsatz.
     * Unterstützt den Verlauf (Stack), wenn bereits ein Einsatz bearbeitet wird.
     */
    const handleAssignmentClick = useCallback((assignment) => {
        if (editingAssignment && editingAssignment.id !== assignment.id) {
            setEditStack(prev => [...prev, editingAssignment]);
            setForwardStack([]); // Vorwärts-Verlauf bei neuem Sprung löschen
        }
        setEditingAssignment(assignment);
        setPresetDate(null);
        setModalOpen(true);
    }, [editingAssignment]);

    /**
     * Navigiert im Verlauf einen Schritt zurück.
     */
    const handleNavigateBack = useCallback(() => {
        if (editStack.length === 0) return;
        const prev = editStack[editStack.length - 1];
        setForwardStack(f => [...f, editingAssignment]);
        setEditStack(p => p.slice(0, -1));
        setEditingAssignment(prev);
    }, [editStack, editingAssignment]);

    /**
     * Navigiert im Verlauf einen Schritt vorwärts.
     */
    const handleNavigateForward = useCallback(() => {
        if (forwardStack.length === 0) return;
        const next = forwardStack[forwardStack.length - 1];
        setEditStack(p => [...p, editingAssignment]);
        setForwardStack(f => f.slice(0, -1));
        setEditingAssignment(next);
    }, [forwardStack, editingAssignment]);

    /**
     * Öffnet den Export-Dialog für einen einzelnen Einsatz (Shortcut).
     */
    const handleExportShortcut = useCallback((assignmentId) => {
        if (allAssignments.length === 0) loadAllAssignments();
        setExportSelectedIds([assignmentId]);
        setExportInitialFilters(null);
        setExportOpen(true);
        setModalOpen(false); // Editor schließen, falls offen
    }, [allAssignments, loadAllAssignments, setExportSelectedIds, setExportInitialFilters, setExportOpen]);

    /**
     * Öffnet den Export-Dialog mit optionalen Filtern und Selektionen.
     */
    const handleOpenExport = useCallback((filters = null, selectedIds = []) => {
        if (allAssignments.length === 0) loadAllAssignments();
        setExportSelectedIds(selectedIds);
        setExportInitialFilters(filters);
        setExportOpen(true);
    }, [allAssignments, loadAllAssignments, setExportSelectedIds, setExportInitialFilters, setExportOpen]);

    /**
     * Initialisiert einen neuen, leeren Einsatz im Editor.
     */
    const handleNewAssignment = useCallback(() => {
        setEditingAssignment(null);
        setPresetDate(null);
        setIsUndatedPreset(false);
        setModalOpen(true);
    }, []);

    /**
     * Initialisiert einen neuen undatierten Einsatz.
     */
    const handleUndatedClick = useCallback(() => {
        setEditingAssignment(null);
        setPresetDate(null);
        setIsUndatedPreset(true);
        setModalOpen(true);
    }, []);

    /**
     * Setzt den Navigations-State zurück (z.B. beim Schließen des Editors).
     */
    const resetNavigation = useCallback(() => {
        setModalOpen(false);
        setEditingAssignment(null);
        setEditStack([]);
        setForwardStack([]);
        setActiveResources({ workers: [], vehicles: [] });
        setActiveDateRange({ start: null, end: null });
    }, []);

    return {
        // State
        editStack,
        setEditStack,
        forwardStack,
        setForwardStack,
        editingAssignment,
        setEditingAssignment,
        modalOpen,
        setModalOpen,
        presetDate,
        setPresetDate,
        isUndatedPreset,
        setIsUndatedPreset,
        navigationSource,
        setNavigationSource,
        activeResources,
        setActiveResources,
        activeDateRange,
        setActiveDateRange,

        // Handlers
        handleAssignmentClick,
        handleNavigateBack,
        handleNavigateForward,
        handleExportShortcut,
        handleOpenExport,
        handleNewAssignment,
        handleUndatedClick,
        resetNavigation
    };
};
